import {objectFromEntries} from '@benchling/js-utils';

import {BenchlingAction} from 'redux-stores/Action';
import {BenchlingState} from 'redux-stores/reduceApp';

import {GlobalClientStateActionTypes} from './GlobalClientStateReduxActions';
import {GlobalClientStateDefinition} from './makeUseGlobalClientState';

export interface GlobalClientStates {
  [globalClientStateUuid: string]: {
    [stateId: string]: unknown;
  };
}

export const INITIAL_GLOBAL_CLIENT_STATES: GlobalClientStates = {};

export class GlobalClientStatesSelector {
  constructor(readonly state: BenchlingState) {
    this.state = state;
  }

  getGlobalClientState<TStateType>(
    {defaultState, globalClientStateUuid}: GlobalClientStateDefinition<TStateType>,
    stateId: string
  ): TStateType {
    const globalClientStates = this.state.ui.globalClientStates;
    if (
      !globalClientStates.hasOwnProperty(globalClientStateUuid) ||
      !globalClientStates[globalClientStateUuid].hasOwnProperty(stateId)
    ) {
      return defaultState;
    }

    return globalClientStates[globalClientStateUuid][stateId] as TStateType;
  }

  getGlobalClientStates<TStateType>(
    globalClientStateDefinition: GlobalClientStateDefinition<TStateType>,
    stateIds: Array<string>
  ): {[stateId: string]: TStateType} {
    return objectFromEntries(
      stateIds.map((stateId) => [stateId, this.getGlobalClientState(globalClientStateDefinition, stateId)])
    );
  }
}

export default function reduceGlobalClientStates(
  state: GlobalClientStates = INITIAL_GLOBAL_CLIENT_STATES,
  action: BenchlingAction
): GlobalClientStates {
  switch (action.type) {
    case GlobalClientStateActionTypes.GLOBAL_CLIENT_STATE_UPDATE: {
      const {
        newState,
        stateId,
        stateDefinition: {globalClientStateUuid},
      } = action;

      return {
        ...state,
        [globalClientStateUuid]: {
          ...state[globalClientStateUuid],
          [stateId]: newState,
        },
      };
    }
    default:
      return state;
  }
}
